import React from "react";
import Modal from "react-modal";
import { useDispatch } from "react-redux";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import { getLinkData, BranchIOLinkData } from "web/script/utils/branch-io";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import withReduxProvider from "web/react/redux-provider";
import { closeCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./app-acquisition-overlay.css";

interface AppAcquisitionOverlayProps {
    captureType: "signup_feed_save" | "signup_du"; // Move these to constants
}

const SAVE_FOR_LATER_LINK = "https://lyst.app.link/4fHObAlxZ3";
const DESIGNER_UPDATE_LINK = "https://lyst.app.link/KpHF8KyBZ3";

export function AppAcquisitionOverlay({
    captureType,
}: AppAcquisitionOverlayProps): null | JSX.Element {
    const dispatch = useDispatch();
    // Depending on the captureType ...
    let messagingCopy;
    let branchLink;
    let trackedClick = useClickTracker(captureType, "click_signup_button", "signup_button");
    switch (captureType) {
        case "signup_du":
            // "Download the App for free to follow new products and deals from this designer."
            messagingCopy = gettext("designer_updates.overlay.app_download");
            branchLink = DESIGNER_UPDATE_LINK;
            break;
        case "signup_feed_save":
        default:
            // "Download the App for free and we’ll let you know as soon as this goes on sale."
            messagingCopy = gettext("save_for_later.overlay.app_download");
            branchLink = SAVE_FOR_LATER_LINK;
            break;
    }
    const branchReferralLink = new URL(branchLink);
    const branchIOLinkData: BranchIOLinkData = getLinkData();

    for (const branchDataProperty in branchIOLinkData) {
        branchReferralLink.searchParams.set(
            branchDataProperty,
            branchIOLinkData[branchDataProperty]
        );
    }

    function triggerClose(): void {
        analytics.event(captureType, "close_app_acquisition_form");
        dispatch(closeCustomerCaptureOverlay());
    }

    function triggerAfterOpen(): void {
        analytics.event(captureType, "open_app_acquisition_form", undefined, true); // non interactive
    }

    let appElement: HTMLElement | undefined;
    if (document.body) {
        appElement = document.body.querySelector<HTMLDivElement>(".site-wrapper") || undefined;
    }

    return (
        <Modal
            appElement={appElement}
            className={styles.modal}
            isOpen={true}
            onAfterOpen={triggerAfterOpen}
            onRequestClose={triggerClose}
            overlayClassName={styles.overlay}
        >
            <div className={styles.content}>
                <div className={styles.brandImage}>
                    <CloseButton className={styles.closeButton} onClick={triggerClose} />
                </div>
                <div className={styles.logoHolder}>
                    <div className={styles.logo} />
                </div>
                <div className={styles.messaging}>
                    <h4 dangerouslySetInnerHTML={{ __html: messagingCopy }}></h4>
                    <a
                        onClick={trackedClick}
                        className={styles.appStoreBadge}
                        href={branchReferralLink.toString()}
                        // "Download on the App Store"
                        title={gettext("general.mobile.image_alt_text")}
                        aria-label={gettext("general.mobile.image_alt_text")}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default withReduxProvider(AppAcquisitionOverlay);
